import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { getCompanyBasicInfoQuery } from "../../Graphql/querries/companyQuery";
import { setCompanyBasicInfo } from "../../store/slices/companySlice";
import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

interface GetCompanyBasicInfoResponse {
  getCompanyBasicInfo: {
    code: string;
    company: { name: string; currencyCode: string } | null;
  };
}

/**
 * Loads company basic info (currency, name) for the current session.
 * Uses getCompanyBasicInfo — available to all authenticated users (not admin-only).
 */
export const useGetCompanyDetails = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation({
    mutationKey: ["getCompanyBasicInfo"],
    mutationFn: async () => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company basic info",
        buildLogContext(
          "company",
          "get-company-basic-info",
          userId,
          companyId,
          undefined
        )
      );

      try {
        const response = await fetchGraphQL<
          GetCompanyBasicInfoResponse,
          Record<string, never>
        >(getCompanyBasicInfoQuery, {}, setAlert);

        const { code, company } = response.getCompanyBasicInfo;

        if (code !== API_RESPONSE_CODE.OK) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Failed to fetch company basic info",
            buildLogContext(
              "company",
              "get-company-basic-info",
              userId,
              companyId,
              undefined,
              {
                apiCode: String(code)
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: code as unknown as ALERT_BANNER_CODE
          });
          return null;
        }

        if (!company?.currencyCode) {
          captureAppException(
            "API returned OK but company currencyCode missing",
            new Error("API returned OK but company currencyCode missing"),
            buildLogContext(
              "company",
              "get-company-basic-info",
              userId,
              companyId,
              undefined
            )
          );
          return null;
        }

        sendAppLog(
          LOG_LEVEL.INFO,
          "Company basic info fetched successfully",
          buildLogContext(
            "company",
            "get-company-basic-info",
            userId,
            companyId,
            undefined,
            {
              companyName: company.name ?? "",
              currencyCode: company.currencyCode
            }
          )
        );

        return {
          currencyCode: company.currencyCode,
          name: company.name ?? ""
        };
      } catch (error) {
        captureAppException(
          "Failed to fetch company basic info",
          error,
          buildLogContext(
            "company",
            "get-company-basic-info",
            userId,
            companyId,
            undefined
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw error; // let mutateAsync() reject so CompanyCurrencyBootstrap's .catch fires
      }
    },
    onSuccess: (payload: { currencyCode: string; name: string } | null) => {
      if (payload) {
        dispatch(
          setCompanyBasicInfo({
            currencyCode: payload.currencyCode,
            name: payload.name
          })
        );
      }
    }
  });
};
